import { Duel, Player } from "~/third-party/ts-elo";

import type {
  ProgressiveDrawDeltaFromCoefficientInput,
  ProgressiveDrawRatingParams,
  RatingAlgorithm,
  RatingInput,
  VariantProgressiveDrawDeltaInput,
} from "./ratingAlgorithm";

export class CurrentRatingAlgorithm implements RatingAlgorithm {
  calculateDelta({
    lifeRating,
    taskRating,
    category,
    retryCountUsed = 0,
    kFactor = 40,
    drawCoefficient,
  }: RatingInput): number {
    const { rw, rl } = this.calculateDuelDeltas(
      lifeRating,
      taskRating,
      kFactor,
    );

    if (category === "loss") {
      if (rw <= 0) return -rl;
      return -Math.min(rl, 3 * rw);
    }

    const scaleDivisor = retryCountUsed + 1;
    if (category === "draw") {
      const coefficient = drawCoefficient ?? 0.5;
      this.validateCoefficient(coefficient);
      return (rw / scaleDivisor) * coefficient;
    }

    return rw / scaleDivisor;
  }

  calculateProgressiveDrawCoefficient({
    totalStudentMoves,
    mistakeStudentMoveNumber,
  }: ProgressiveDrawRatingParams): number {
    if (!Number.isInteger(totalStudentMoves) || totalStudentMoves <= 0) {
      throw new Error("totalStudentMoves must be a positive integer");
    }

    if (mistakeStudentMoveNumber === undefined) {
      return 0.5;
    }

    if (
      !Number.isInteger(mistakeStudentMoveNumber) ||
      mistakeStudentMoveNumber <= 0
    ) {
      throw new Error("mistakeStudentMoveNumber must be a positive integer");
    }

    if (mistakeStudentMoveNumber > totalStudentMoves) {
      throw new Error("mistakeStudentMoveNumber cannot exceed totalStudentMoves");
    }

    if (totalStudentMoves === 1) {
      return 0.5;
    }

    return (
      0.5 +
      ((mistakeStudentMoveNumber - 2) / (totalStudentMoves - 1)) * 0.5
    );
  }

  calculateProgressiveDrawDeltaFromCoefficient({
    lifeRating,
    taskRating,
    retryCountUsed,
    kFactor,
    coefficient,
  }: ProgressiveDrawDeltaFromCoefficientInput): number {
    this.validateCoefficient(coefficient);

    return (
      this.calculateDelta({
        lifeRating,
        taskRating,
        category: "win",
        retryCountUsed,
        kFactor,
      }) * coefficient
    );
  }

  calculateVariantProgressiveDrawDelta({
    lifeRating,
    taskRating,
    retryCountUsed,
    kFactor,
    params,
  }: VariantProgressiveDrawDeltaInput): number {
    const { variantCount, coefficientSum } = params;

    if (!Number.isInteger(variantCount) || variantCount <= 0) {
      throw new Error("variantCount must be a positive integer");
    }

    if (!Number.isFinite(coefficientSum)) {
      throw new Error("coefficientSum must be finite");
    }

    if (coefficientSum < 0) {
      throw new Error("coefficientSum must be non-negative");
    }

    if (coefficientSum > variantCount) {
      throw new Error("coefficientSum cannot exceed variantCount");
    }

    return this.calculateProgressiveDrawDeltaFromCoefficient({
      lifeRating,
      taskRating,
      retryCountUsed,
      kFactor,
      coefficient: coefficientSum / variantCount,
    });
  }

  private calculateDuelDeltas(
    lifeRating: number,
    taskRating: number,
    kFactor: number,
  ): { rw: number; rl: number } {
    const matchWin = new Duel({ kFactor });
    matchWin.addPlayer(new Player("user", lifeRating), true);
    matchWin.addPlayer(new Player("task", taskRating), false);
    const winResult = matchWin.calculate();
    let userNewWinRating = lifeRating;
    winResult.results.forEach((result) => {
      if (result.identifier === "user") userNewWinRating = result.rating;
    });

    const matchLoss = new Duel({ kFactor });
    matchLoss.addPlayer(new Player("user", lifeRating), false);
    matchLoss.addPlayer(new Player("task", taskRating), true);
    const lossResult = matchLoss.calculate();
    let userNewLossRating = lifeRating;
    lossResult.results.forEach((result) => {
      if (result.identifier === "user") userNewLossRating = result.rating;
    });

    return {
      rw: userNewWinRating - lifeRating,
      rl: Math.abs(userNewLossRating - lifeRating),
    };
  }

  private validateCoefficient(coefficient: number): void {
    if (!Number.isFinite(coefficient)) {
      throw new Error("coefficient must be finite");
    }

    if (coefficient < 0) {
      throw new Error("coefficient must be non-negative");
    }
  }
}
